import {
  isPossiblePhoneNumber,
  parsePhoneNumber,
} from "react-phone-number-input";

/* The billing-address rules, extracted VERBATIM. */

export const sanitizeInput = (input) => {
  if (!input) return '';
  // Remove any HTML tags and special characters that could be used for XSS/SQL injection
  return String(input)
    .replace(/[<>]/g, '') // Remove < and >
    .replace(/[&]/g, '&amp;')
    .replace(/['"]/g, '')
    .trim();
};

export const validateAddressLine = (value, fieldName) => {
  if (!value || value.trim() === '') {
    return `${fieldName} is required`;
  }
  
  const sanitized = sanitizeInput(value);
  if (sanitized.length < 2) {
    return `${fieldName} must be at least 2 characters long`;
  }
  
  if (sanitized.length > 200) {
    return `${fieldName} must not exceed 200 characters`;
  }
  
  // Check for SQL injection patterns
  const sqlPattern = /(\bSELECT\b|\bINSERT\b|\bUPDATE\b|\bDELETE\b|\bDROP\b|\bUNION\b|\b--\b|;|\bOR\b|\bAND\b)/i;
  if (sqlPattern.test(sanitized)) {
    return `Invalid characters in ${fieldName}`;
  }
  
  // Check for allowed characters (alphanumeric, spaces, hyphens, periods, commas, #, /)
  const allowedPattern = /^[a-zA-Z0-9\s\-.,#/]+$/;
  if (!allowedPattern.test(sanitized)) {
    return `${fieldName} contains invalid characters`;
  }
  
  return null;
};

export const validatePostalCode = (value, countryCode) => {
  if (!value || value.trim() === '') {
    return null; // Postal code can be optional
  }
  
  const sanitized = sanitizeInput(value);
  
  // Country-specific postal code patterns
  const patterns = {
    'AE': /^[0-9]{3,5}$/,
    'US': /^[0-9]{5}(-[0-9]{4})?$/,
    'GB': /^[A-Z]{1,2}[0-9][A-Z0-9]? ?[0-9][A-Z]{2}$/i,
    'CA': /^[A-Z][0-9][A-Z] ?[0-9][A-Z][0-9]$/i,
    'AU': /^[0-9]{4}$/,
    'IN': /^[0-9]{6}$/,
    'DE': /^[0-9]{5}$/,
    'FR': /^[0-9]{5}$/,
    'IT': /^[0-9]{5}$/,
    'ES': /^[0-9]{5}$/,
    'JP': /^[0-9]{3}-[0-9]{4}$/,
    'BR': /^[0-9]{5}-[0-9]{3}$/,
  };
  
  const pattern = patterns[countryCode];
  if (pattern && !pattern.test(sanitized)) {
    return `Invalid postal code format for selected country`;
  }
  
  if (sanitized.length > 20) {
    return 'Postal code must not exceed 20 characters';
  }
  
  return null;
};

export const validateCompanyName = (value) => {
  if (!value) return null;
  
  const sanitized = sanitizeInput(value);
  
  if (sanitized.length < 2) {
    return 'Business name must be at least 2 characters long';
  }

  if (sanitized.length > 100) {
    return 'Business name must not exceed 100 characters';
  }

  // Allow letters, numbers, spaces, dots, hyphens, and ampersands
  const allowedPattern = /^[a-zA-Z0-9\s\.\-&]+$/;
  if (!allowedPattern.test(sanitized)) {
    return 'Business name can only contain letters, numbers, spaces, dots (.), hyphens (-), and ampersands (&)';
  }

  // Check for SQL injection patterns
  const sqlPattern = /(\bSELECT\b|\bINSERT\b|\bUPDATE\b|\bDELETE\b|\bDROP\b|\bUNION\b|\b--\b|;|\bOR\b|\bAND\b)/i;
  if (sqlPattern.test(sanitized)) {
    return 'Business name contains invalid characters or keywords';
  }

  // Additional check: no multiple consecutive spaces
  if (/\s{2,}/.test(sanitized)) {
    return 'Business name should not contain multiple consecutive spaces';
  }

  // Check that company name doesn't start or end with special characters
  if (/^[\s\.\-&]|[\s\.\-&]$/.test(sanitized)) {
    return 'Business name should not start or end with spaces, dots, hyphens, or ampersands';
  }
  
  return null;
};

export const validateTRNNumber = (value) => {
  if (!value) return null;
  
  const sanitized = sanitizeInput(value);
  
  if (sanitized.length > 50) {
    return 'Tax Registration Number must not exceed 50 characters';
  }
  
  // Allow alphanumeric, hyphens, and spaces
  const allowedPattern = /^[a-zA-Z0-9\s\-]+$/;
  if (!allowedPattern.test(sanitized)) {
    return 'Tax Registration Number contains invalid characters';
  }
  
  return null;
};

export const validateCity = (value) => {
  if (!value || value.trim() === '') {
    return 'City is required';
  }
  
  const sanitized = sanitizeInput(value);
  
  if (sanitized.length < 2) {
    return 'City must be at least 2 characters long';
  }
  
  if (sanitized.length > 100) {
    return 'City must not exceed 100 characters';
  }
  
  // Allow letters, spaces, hyphens, and periods
  const allowedPattern = /^[a-zA-Z\s\-.'()]+$/;
  if (!allowedPattern.test(sanitized)) {
    return 'City contains invalid characters';
  }
  
  return null;
};

export const validateState = (value, statesList) => {
  if (!value || value.trim() === '') {
    return 'State is required';
  }
  
  const sanitized = sanitizeInput(value);
  
  if (statesList && statesList.length > 0) {
    // If states are from dropdown, validate against list
    const isValidState = statesList.some(state => 
      state.name.toLowerCase() === sanitized.toLowerCase()
    );
    if (!isValidState) {
      return 'Please select a valid state from the list';
    }
  } else {
    // Free text state validation
    if (sanitized.length < 2) {
      return 'State must be at least 2 characters long';
    }
    
    if (sanitized.length > 100) {
      return 'State must not exceed 100 characters';
    }
    
    const allowedPattern = /^[a-zA-Z\s\-.'()]+$/;
    if (!allowedPattern.test(sanitized)) {
      return 'State contains invalid characters';
    }
  }
  
  return null;
};

/* The dial code the API stores as `country_code`. */
export const dialCodeOf = (mobile) => {
  if (!mobile) return "";
  try {
    const parsed = parsePhoneNumber(String(mobile));
    return parsed?.countryCallingCode ? `+${parsed.countryCallingCode}` : "";
  } catch {
    /* An unparseable number is already rejected by validateMobileNumber; the
       caller falls back to whatever was stored. */
    return "";
  }
};

export const validateMobileNumber = (mobile, countryCode) => {
  if (!mobile) {
    return 'Mobile Number is required';
  }
  
  if (!isPossiblePhoneNumber(mobile?.toString() ?? "")) {
    return 'Enter a valid mobile number';
  }
  
  try {
    const parsedNumber = parsePhoneNumber(mobile);
    if (parsedNumber && countryCode) {
      const callingCode = parsedNumber.countryCallingCode;
      // Additional validation can be added here if needed
    }
  } catch (error) {
    return 'Enter a valid mobile number';
  }
  
  return null;
};
